// 商户门户审批/通知提示音轮询 hook（fleet sound-merchant 2026-09-28）：
// 60s 轮询两条异质链，逐链独立判定，任一链「可信增加」即响（两链异质禁合计，
// 合计会在一增一减时漏报）：
//   - 链 N：消息未读数 GET unread-count（D3 口径全类型未读总数，fetchNoticeUnreadCount）
//   - 链 A：审批中心待办 APC-002 五类型 pending 总数（fetchPendingApprovalCount + approvalBadgeCount）
// 提示音口径 canonical = web/admin/src/lib/reviewSound.ts（纯函数复用 lib/noticeSound.ts）。
// 链失败/非 ok/403 → 该链计数不可信（null），该链不响、不更新快照；各链首快照不响。
// 单实例挂载：仅 Sidebar.tsx（认证布局内常驻）。⚠️ Header.tsx 与 Sidebar.tsx 均调用
// useMerchantBadges（双消费），但提示音不挂在 useMerchantBadges 内——本 hook 只在
// Sidebar 挂载一次，禁止在 Header/App 等处重复挂载导致双响。
import { useEffect, useRef } from 'react';
import { fetchNoticeUnreadCount } from '@/api/notices';
import { fetchPendingApprovalCount } from '@/api/approvals';
import { approvalBadgeCount } from '@/api/approvalsCore';
import { playNoticeSound, shouldAlertNoticeIncrease } from '@/lib/noticeSound';

/** 提示音轮询间隔：新审批/新通知到达后 60s 内响（与 admin useReviewBadges 同间隔） */
const NOTICE_POLL_MS = 60_000;

export function useNoticeSound(): void {
  /** 链 N 上次「可信」未读数快照；null = 尚无可信快照（不响） */
  const lastTrustedNoticeRef = useRef<number | null>(null);
  /** 链 A 上次「可信」审批待办数快照；null = 尚无可信快照（不响） */
  const lastTrustedApprovalRef = useRef<number | null>(null);

  useEffect(() => {
    // 竞态保护：卸载后返回的旧响应不得再判定/写快照
    let stale = false;

    const loadNotice = async () => {
      let count: number | null = null;
      try {
        const res = await fetchNoticeUnreadCount();
        if (stale) return;
        // ok:false（401/5xx 等）→ null 不可信
        count = res.ok ? (res.data.unreadCount ?? null) : null;
      } catch {
        count = null; // 网络异常 → 不可信
      }
      if (stale) return;
      if (shouldAlertNoticeIncrease(lastTrustedNoticeRef.current, count)) {
        playNoticeSound();
      }
      if (count != null) {
        lastTrustedNoticeRef.current = count;
      }
    };

    const loadApprovals = async () => {
      let count: number | null = null;
      try {
        const res = await fetchPendingApprovalCount();
        if (stale) return;
        // APC-002 total 优先、byType 五键求和兜底（与 useMerchantBadges 同口径）；
        // 形状不可解析/403 → null 不可信
        count = res.ok ? approvalBadgeCount(res.data) : null;
      } catch {
        count = null; // 网络异常 → 不可信
      }
      if (stale) return;
      if (shouldAlertNoticeIncrease(lastTrustedApprovalRef.current, count)) {
        playNoticeSound();
      }
      if (count != null) {
        lastTrustedApprovalRef.current = count;
      }
    };

    const loadAll = () => {
      void loadNotice();
      void loadApprovals();
    };

    loadAll();
    const timer = window.setInterval(loadAll, NOTICE_POLL_MS);

    return () => {
      stale = true;
      window.clearInterval(timer);
    };
  }, []);
}
